import { useState, useRef, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore, useStudioStore } from '@/store'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import { confirmLeaveUnsavedChanges } from '@/lib/unsavedChanges'
import { Studio } from '@/types'
import { ChevronDown, Building2, BarChart3, MapPin } from 'lucide-react'

interface HeaderStudioSelectorProps {
  studios: Studio[]
  currentStudioId: number | null
  compact?: boolean
}

export function HeaderStudioSelector({ studios, currentStudioId, compact = false }: HeaderStudioSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [accessLevel, setAccessLevel] = useState<'standard' | 'advanced' | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { setSelectedStudio } = useStudioStore()
  const { user } = useAuthStore()
  const { getAccessLevel } = useManagementCompanySimple(user?.id)
  const isManagementCompany = studios.some(
    (studio) => studio.access_kind === 'management_company' || studio.role === 'management_company'
  )

  useEffect(() => {
    const loadAccessLevel = async () => {
      if (!isManagementCompany || !user?.id) {
        setAccessLevel(null)
        return
      }
      const level = await getAccessLevel()
      setAccessLevel(level)
    }

    loadAccessLevel()
  }, [isManagementCompany, user?.id, getAccessLevel])

  const filteredStudios = useMemo(() => {
    if (!isManagementCompany || accessLevel !== 'advanced') {
      return studios
    }

    const visibleStatuses = new Set(['OPEN', 'PRE-OPEN', 'SERVICE'])
    const base = studios.filter((studio) => visibleStatuses.has(studio.status_code || 'OPEN'))

    if (currentStudioId && !base.some((studio) => studio.studio_internal_id === currentStudioId)) {
      const selected = studios.find((studio) => studio.studio_internal_id === currentStudioId)
      return selected ? [selected, ...base] : base
    }

    return base
  }, [studios, isManagementCompany, accessLevel, currentStudioId])

  const sortedStudios = useMemo(() => {
    return [...filteredStudios].sort((a, b) => {
      const cityA = (a.city?.city_name || '').trim()
      const cityB = (b.city?.city_name || '').trim()
      const byCity = cityA.localeCompare(cityB, 'ru', { sensitivity: 'base' })
      if (byCity !== 0) return byCity

      return String(a.studio_number).localeCompare(String(b.studio_number), 'ru', { numeric: true, sensitivity: 'base' })
    })
  }, [filteredStudios])

  const cityGroups = useMemo(() => {
    const map = new Map<number, { cityName: string; studios: typeof sortedStudios }>()
    for (const studio of sortedStudios) {
      const cId = studio.city_id
      const cityName = studio.city?.city_name || 'Неизвестный город'
      if (!map.has(cId)) {
        map.set(cId, { cityName, studios: [] })
      }
      map.get(cId)!.studios.push(studio)
    }
    return Array.from(map.entries())
      .map(([cId, { cityName, studios }]) => ({ cityId: cId, cityName, studios }))
      .sort((a, b) => a.cityName.localeCompare(b.cityName, 'ru', { sensitivity: 'base' }))
  }, [sortedStudios])

  const hasMultipleCities = cityGroups.length > 1

  // Закрываем dropdown при клике вне его
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Находим текущую студию
  const currentStudio = studios.find(s => s.studio_internal_id === currentStudioId)
  const currentStudioLabel = `${currentStudio?.city?.city_name || 'Студия'} №${currentStudio?.studio_number || ''}`
  const currentStudioAddress = currentStudio?.address?.trim() || ''
  const isAnalyticsPath = location.pathname.includes('/analytics') || location.pathname.includes('/clients')

  if (!currentStudio) {
    return null
  }

  // Определяем текущий под-роут студии для сохранения контекста при переключении.
  const getCurrentPath = () => {
    const studioRouteMatch = location.pathname.match(/^\/partners\/[^/]+\/studios\/[^/]+\/([^/?#]+)/)
    const segment = studioRouteMatch?.[1]
    const allowedSegments = new Set([
      'metrics',
      'history',
      'finance',
      'analytics',
      'clients',
      'ratings',
      'teachers',
      'trainer-reviews',
      'management',
      'studio-metrics',
      'studio-metrics-grid',
      'plan-history'
    ])

    if (segment === 'history') {
      return 'studio-metrics-grid'
    }

    if (segment && allowedSegments.has(segment)) {
      return segment
    }

    return 'analytics'
  }

  const handleStudioChange = (studioId: number) => {
    if (!confirmLeaveUnsavedChanges()) return
    const studio = studios.find(s => s.studio_internal_id === studioId)
    if (studio) {
      setSelectedStudio(studio)
    }
    const path = getCurrentPath()
    const partnerMatch = location.pathname.match(/^\/partners\/([^/]+)\/studios\/[^/]+\/.+/)
    const partnerNumber = partnerMatch?.[1] || (studio?.partner_number != null ? String(studio.partner_number) : null)

    if (partnerNumber) {
      navigate(`/partners/${partnerNumber}/studios/${studioId}/${path}`)
    } else {
      navigate('/partners')
    }
    setIsOpen(false)
  }

  const handleCityChange = (cityId: number) => {
    if (!confirmLeaveUnsavedChanges()) return
    const partnerMatch = location.pathname.match(/^\/partners\/([^/]+)\//)
    const partnerNumber = partnerMatch?.[1]
    if (partnerNumber) {
      navigate(`/partners/${partnerNumber}/cities/${cityId}/analytics`)
    }
    setIsOpen(false)
  }

  const handleAllStudios = () => {
    if (!confirmLeaveUnsavedChanges()) return
    const partnerMatch = location.pathname.match(/^\/partners\/([^/]+)\//)
    const partnerNumber = partnerMatch?.[1]
    if (partnerNumber) {
      navigate(`/partners/${partnerNumber}/analytics`)
    } else {
      navigate('/analytics')
    }
    setIsOpen(false)
  }

  // Для не-аналитических экранов при одной студии оставляем простой текст
  if (studios.length <= 1 && !isAnalyticsPath) {
    return (
      <p className="text-muted-foreground leading-tight">
        <span className={`block truncate ${compact ? 'text-xs' : 'text-sm'}`}>{currentStudioLabel}</span>
        {!compact && currentStudioAddress && (
          <span className="block text-sm truncate">{currentStudioAddress}</span>
        )}
      </p>
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full text-left text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 group ${compact ? 'text-xs' : 'text-sm'}`}
      >
        <span className="block min-w-0 text-left leading-tight">
          <span className="block truncate">{currentStudioLabel}</span>
          {!compact && currentStudioAddress && (
            <span className="block text-xs truncate">{currentStudioAddress}</span>
          )}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform group-hover:text-foreground shrink-0 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-2 w-auto min-w-[280px] bg-card border border-border rounded-lg shadow-lg overflow-hidden z-50">
          <div className="max-h-[400px] overflow-y-auto">
            {hasMultipleCities ? (
              <>
                {cityGroups.map((group, index) => (
                  <div key={group.cityId}>
                    {index > 0 && <div className="border-t border-border/50" />}

                    {/* City aggregate row — only when city has 2+ studios */}
                    {group.studios.length > 1 && (
                      <button
                        onClick={() => handleCityChange(group.cityId)}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 transition-colors text-left"
                      >
                        <MapPin className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">
                            {group.cityName} — свод ({group.studios.length} студи{group.studios.length < 5 ? 'и' : 'й'})
                          </div>
                        </div>
                      </button>
                    )}

                    {/* Studios in city */}
                    {group.studios.map((studio) => {
                      const isSelected = studio.studio_internal_id === currentStudioId
                      return (
                        <button
                          key={studio.studio_internal_id}
                          onClick={() => handleStudioChange(studio.studio_internal_id)}
                          className={`w-full flex items-center gap-2 px-3 py-2 transition-colors text-left ${isSelected ? 'bg-primary/10' : 'hover:bg-muted/50'}`}
                        >
                          <Building2 className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-medium truncate ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                              {studio.city?.city_name || 'Студия'} №{studio.studio_number}
                            </div>
                            {studio.address && (
                              <div className="text-xs text-muted-foreground truncate">
                                {studio.address}
                              </div>
                            )}
                          </div>
                          {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />}
                        </button>
                      )
                    })}
                  </div>
                ))}
              </>
            ) : (
              /* Single city — flat list */
              sortedStudios.map((studio) => {
                const isSelected = studio.studio_internal_id === currentStudioId
                return (
                  <button
                    key={studio.studio_internal_id}
                    onClick={() => handleStudioChange(studio.studio_internal_id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 transition-colors text-left ${isSelected ? 'bg-primary/10' : ''}`}
                  >
                    <Building2 className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                        {studio.city?.city_name || 'Студия'} №{studio.studio_number}
                      </div>
                      {studio.address && (
                        <div className="text-xs text-muted-foreground truncate">
                          {studio.address}
                        </div>
                      )}
                    </div>
                    {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />}
                  </button>
                )
              })
            )}

            {/* Свод по всем студиям внизу */}
            <button
              onClick={handleAllStudios}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 transition-colors text-left border-t border-border"
            >
              <BarChart3 className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
              <div className="flex-1">
                <div className="text-sm font-medium text-foreground">
                  Сводная аналитика
                </div>
                <div className="text-xs text-muted-foreground">
                  По всем студиям франчайзи
                </div>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
